import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, MoreVertical, Copy, Trash2, Pencil, ExternalLink, Globe, FolderKanban, Coins, Crown } from "lucide-react";
import { toast } from "sonner";
import { api, fmtDate, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card } from "@/pages/AppLayout";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

export function ProjectCard({ p, onChanged, i }) {
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState(false);
  const [rename, setRename] = useState(null);

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); onChanged(); } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="group relative rounded-2xl border border-[#222B40] bg-[#111622] hover:border-blue-500/40 transition-colors rise" style={{ animationDelay: `${i * 40}ms` }} data-testid={`project-card-${p.id}`}>
      <button onClick={() => navigate(`/app/proyecto/${p.id}`)} className="block w-full text-left p-6" data-testid={`project-open-${p.id}`}>
        <div className="flex items-center gap-2 mb-4">
          <span className="font-mono text-xs text-slate-500">v{p.current_version}</span>
          {p.published && <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">Publicada</span>}
        </div>
        <h3 className="text-lg font-medium text-white pr-8 truncate">{p.name}</h3>
        <p className="mt-2 text-sm text-slate-400 line-clamp-2 min-h-[2.5rem]">{p.description || "Sin descripción"}</p>
        <p className="mt-5 text-xs text-slate-500">Actualizado {fmtDate(p.updated_at)}</p>
      </button>
      <div className="absolute top-4 right-4">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/5" data-testid={`project-menu-${p.id}`}><MoreVertical className="w-4 h-4" /></button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-[#161C2E] border-[#222B40]">
            <DropdownMenuItem onClick={() => setRename(p.name)} data-testid={`project-rename-${p.id}`}><Pencil className="w-4 h-4 mr-2" />Renombrar</DropdownMenuItem>
            <DropdownMenuItem onClick={() => act(() => api.post(`/projects/${p.id}/duplicate`), "Proyecto duplicado")} data-testid={`project-duplicate-${p.id}`}><Copy className="w-4 h-4 mr-2" />Duplicar</DropdownMenuItem>
            {p.published && <DropdownMenuItem onClick={() => window.open(`/p/${p.slug}`, "_blank")} data-testid={`project-visit-${p.id}`}><ExternalLink className="w-4 h-4 mr-2" />Abrir publicada</DropdownMenuItem>}
            <DropdownMenuItem onClick={() => setConfirm(true)} className="text-red-400" data-testid={`project-delete-${p.id}`}><Trash2 className="w-4 h-4 mr-2" />Eliminar</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent className="bg-[#161C2E] border-[#222B40]">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar «{p.name}»?</AlertDialogTitle>
            <AlertDialogDescription>Se borrarán el proyecto, su conversación y todas sus versiones. Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-500" data-testid="delete-confirm-btn"
              onClick={() => act(() => api.delete(`/projects/${p.id}`), "Proyecto eliminado")}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Dialog open={rename !== null} onOpenChange={(o) => !o && setRename(null)}>
        <DialogContent className="bg-[#161C2E] border-[#222B40]">
          <DialogHeader><DialogTitle>Renombrar proyecto</DialogTitle></DialogHeader>
          <Input value={rename || ""} onChange={(e) => setRename(e.target.value)} data-testid="rename-input" />
          <DialogFooter>
            <button className="px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-400 text-white text-sm" data-testid="rename-save-btn"
              onClick={() => act(() => api.patch(`/projects/${p.id}`, { name: rename }), "Nombre actualizado").then(() => setRename(null))}>Guardar</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, testId, to }) => (
  <Link to={to} className="block"><Card className="hover:border-blue-500/40 transition-colors">
    <Icon className="w-5 h-5 text-cyan-400" />
    <p className="mt-4 text-xs uppercase tracking-widest text-slate-500">{label}</p>
    <p className="mt-1 font-display text-2xl font-bold text-white" data-testid={testId}>{value}</p>
  </Card></Link>
);

export default function Projects() {
  const { user, refreshUser } = useAuth();
  const [projects, setProjects] = useState(null);
  const load = useCallback(() => { api.get("/projects").then((r) => setProjects(r.data)); refreshUser(); }, [refreshUser]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="projects-page">
      <PageHeader eyebrow="Espacio de trabajo" title={`Hola, ${user.name.split(" ")[0]}`} subtitle="Aquí están todas tus aplicaciones."
        action={<Link to="/app/nuevo" data-testid="new-project-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-blue-500 hover:bg-blue-400 text-white font-medium text-sm transition-colors"><Plus className="w-4 h-4" />Nueva aplicación</Link>} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
        <Stat icon={FolderKanban} label="Proyectos" value={projects?.length ?? "…"} testId="stat-projects" to="/app" />
        <Stat icon={Globe} label="Publicadas" value={projects ? projects.filter((p) => p.published).length : "…"} testId="stat-published" to="/app/publicadas" />
        <Stat icon={Coins} label="Créditos" value={user.credits} testId="stat-credits" to="/app/creditos" />
        <Stat icon={Crown} label="Plan" value={user.plan === "sin_plan" ? "Sin plan" : user.plan} testId="stat-plan" to="/app/plan" />
      </div>
      <h2 className="text-lg font-medium text-white mb-5">Mis proyectos</h2>
      {projects && projects.length === 0 && (
        <Card className="text-center py-16 border-dashed" data-testid="projects-empty">
          <p className="text-slate-300">Aún no tienes proyectos.</p>
          <Link to="/app/nuevo" className="mt-4 inline-block text-cyan-400 hover:underline text-sm" data-testid="empty-new-project-link">Crea tu primera aplicación →</Link>
        </Card>
      )}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {projects?.map((p, i) => <ProjectCard key={p.id} p={p} i={i} onChanged={load} />)}
      </div>
    </div>
  );
}
